import { useState, useEffect, useCallback } from 'react';
import api from 'api';
import { InvoiceResponse } from 'api/models/response/Invoices';
import DashboardStats from '../DashboardStats/DashboardStats';
import DateFilterTotal from '../DateFilterTotal/DateFilterTotal';
import InvoicesTable from '../InvoicesTable/InvoicesTable';
import { PaginatedItems } from 'components/common/pagination';
import * as S from './ClientInvoicesView.styled';

export default function ClientInvoicesView() {
    const [clientsAppsForTable, setClientsAppsForTable] = useState<InvoiceResponse[]>([]);
    const [invoices, setInvoices] = useState<InvoiceResponse[]>([]);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalRecords: 0,
        totalPages: 0,
    });

    const [stats, setStats] = useState([
        { label: 'Sent to Business', value: 0 },
        { label: 'Sent to Trust', value: 0, change: '5.2%' },
        { label: 'Paid', value: 0, change: '2.3%' },
        { label: 'Partially Paid', value: 0 },
        { label: 'Pending', value: 0, change: '2.3%' },
        { label: 'Pending Amount', value: 0, currency: '$' },
        { label: 'Fully Paid Amount', value: 0, change: '1.4%', currency: '$' },
    ]);

    const getInvoices = useCallback(async (filters?: Record<string, unknown>) => {
        try {
            const resp = await api.invoices.get(filters);
            if (resp?.status === 200 || resp?.status === 201) {
                setInvoices(resp.data);
                const xPagination = resp.headers?.['x-pagination'];
                if (xPagination) {
                    const xPaginationObj = JSON.parse(xPagination.replace(/'/g, '"'));
                    setPagination({
                        page: xPaginationObj.page,
                        pageSize: xPaginationObj.pageSize,
                        totalRecords: xPaginationObj.TotalRecords,
                        totalPages: xPaginationObj.TotalPages,
                    });

                    setStats((prevStats) =>
                        prevStats.map((stat, index: number) => {
                            let newValue = stat.value;

                            switch (index) {
                                case 0:
                                    newValue = xPaginationObj.BusinessAccountInvoices;
                                    break;
                                case 1:
                                    newValue = xPaginationObj.TrustAccountInvoices;
                                    break;
                                case 2:
                                    newValue = xPaginationObj.PaidInvoices;
                                    break;
                                case 3:
                                    newValue = xPaginationObj.PartiallyPaidInvoices;
                                    break;
                                case 4:
                                    newValue = xPaginationObj.PendingInvoices;
                                    break;
                                case 5:
                                    newValue = xPaginationObj.AmountPending;
                                    break;
                                case 6:
                                    newValue = xPaginationObj.AmountFullyPaid;
                                    break;
                                default:
                                    break;
                            }

                            return { ...stat, value: newValue };
                        })
                    );
                }
            }
        } catch (err) {
            console.log(err);
        }
    }, []);

    useEffect(() => {
        getInvoices();
    }, [getInvoices]);

    const handlePageNumber = (newPage: number) => {
        getInvoices({ page: newPage, pageSize: pagination.pageSize });
    };

    const [filters, setFilters] = useState({});
    useEffect(() => {
        getInvoices(filters);
    }, [filters, getInvoices]);

    const handleEdit = (from: Date, to: Date) => {
        const from_date = from.toISOString().split('T')[0];
        const to_date = to.toISOString().split('T')[0];
        setFilters((prevFilters) => ({
            ...prevFilters,
            from_date: from_date || null,
            to_date: to_date || null,
        }));
    };

    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const invoiceListRefetch = () => {
        getInvoices(filters);
    };

    return (
        <>
            <S.Description>Invoices you manage for your own clients</S.Description>
            <S.TopBanner>
                <DateFilterTotal
                    fromDate={formatDate(startOfMonth)}
                    toDate={formatDate(today)}
                    totalAmount={
                        (stats.find((e) => e.label === 'Partially Paid')?.value ?? 0) +
                        (stats.find((e) => e.label === 'Fully Paid Amount')?.value ?? 0)
                    }
                    onEdit={handleEdit}
                />
            </S.TopBanner>
            <DashboardStats stats={stats}></DashboardStats>
            <InvoicesTable
                mobile={false}
                invoices={invoices}
                invoicesForTable={clientsAppsForTable}
                setInvoicesForTable={setClientsAppsForTable}
                isClientsAppsLoading={false}
                onFilterChange={setFilters}
                invoiceListRefetch={invoiceListRefetch}
            />
            <span
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '92vw',
                }}
            >
                <PaginatedItems
                    itemsPerPage={pagination.pageSize}
                    numberOfItems={pagination.totalRecords}
                    handleClick={handlePageNumber}
                    pageNumber={pagination.page}
                />
            </span>
        </>
    );
}
